import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  ExternalLink,
  Copy,
  ShoppingBag,
  Image as ImageIcon,
} from 'lucide-react';

const AdminMerchantFeed = () => {
  const { getAuthHeaders } = useAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | blocked | ok

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/feed-diagnostics', { headers: getAuthHeaders() });
      setData(res.data);
    } catch {
      toast.error('Kunde inte hämta feed-diagnostik');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const copyFeedUrl = () => {
    if (!data?.feed_url) return;
    navigator.clipboard.writeText(data.feed_url);
    toast.success('Feed-URL kopierad');
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2a9d8f]" />
      </div>
    );
  }

  const filtered = data.items.filter((it) => {
    if (filter === 'blocked') return !it.eligible;
    if (filter === 'ok') return it.eligible;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="admin-merchant-feed">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShoppingBag className="w-7 h-7 text-[#2a9d8f]" />
            Google Merchant — Feed-status
          </h1>
          <p className="text-slate-500 mt-1 max-w-2xl">
            Detta är produkterna som skickas till Google Shopping. Blockerade produkter ingår
            <strong> inte </strong> i flödet eftersom Google annars skulle neka dem (kläder utan
            storlek/färg, kort beskrivning eller saknad bild).
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" data-testid="refresh-feed-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Uppdatera
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button
          type="button"
          onClick={() => setFilter('all')}
          className={`p-5 rounded-xl border text-left transition-all ${
            filter === 'all' ? 'border-[#2a9d8f] bg-[#2a9d8f]/5' : 'border-slate-200 bg-white hover:border-slate-300'
          }`}
          data-testid="filter-all-btn"
        >
          <div className="text-sm text-slate-500">Totalt</div>
          <div className="text-3xl font-semibold text-slate-900 mt-1">{data.total}</div>
        </button>
        <button
          type="button"
          onClick={() => setFilter('ok')}
          className={`p-5 rounded-xl border text-left transition-all ${
            filter === 'ok' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'
          }`}
          data-testid="filter-ok-btn"
        >
          <div className="text-sm text-emerald-600 flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4" /> Godkänns av Google
          </div>
          <div className="text-3xl font-semibold text-emerald-700 mt-1">{data.eligible}</div>
        </button>
        <button
          type="button"
          onClick={() => setFilter('blocked')}
          className={`p-5 rounded-xl border text-left transition-all ${
            filter === 'blocked' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 bg-white hover:border-slate-300'
          }`}
          data-testid="filter-blocked-btn"
        >
          <div className="text-sm text-amber-700 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" /> Blockeras — behöver fixas
          </div>
          <div className="text-3xl font-semibold text-amber-700 mt-1">{data.blocked}</div>
        </button>
      </div>

      {/* Feed URL */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="text-sm font-medium text-slate-700 mb-2">Feed-URL (klistra in i Google Merchant Center)</div>
        <div className="flex items-center gap-2">
          <code className="flex-1 px-3 py-2 text-sm bg-slate-50 rounded border border-slate-200 truncate">
            {data.feed_url}
          </code>
          <Button onClick={copyFeedUrl} variant="outline" size="sm" data-testid="copy-feed-url-btn">
            <Copy className="w-4 h-4" />
          </Button>
          <a href={data.feed_url} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm">
              <ExternalLink className="w-4 h-4" />
            </Button>
          </a>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Ange denna URL i Merchant Center → Datakällor → Lägg till produkter → Schemalagd hämtning.
        </p>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 text-sm font-medium text-slate-700">
          {filter === 'blocked' && 'Produkter som blockeras'}
          {filter === 'ok' && 'Produkter som ingår i flödet'}
          {filter === 'all' && 'Alla produkter'} — {filtered.length} st
        </div>
        <div className="divide-y divide-slate-200">
          {filtered.map((it) => (
            <div
              key={it.product_id}
              className="p-4 flex items-start gap-4 hover:bg-slate-50/50"
              data-testid={`feed-row-${it.product_id}`}
            >
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 flex items-center justify-center">
                {it.image ? (
                  <img src={it.image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-6 h-6 text-slate-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium text-slate-900 truncate">{it.name || '(namnlös produkt)'}</div>
                  {it.eligible ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">
                      <CheckCircle2 className="w-3 h-3" /> OK
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                      <AlertTriangle className="w-3 h-3" /> Blockerad
                    </span>
                  )}
                  {it.is_apparel && (
                    <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded-full">Kläder</span>
                  )}
                  {it.hidden && (
                    <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded-full">Dold</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {it.category || 'okänd kategori'} · {it.price ? `${Number(it.price).toFixed(0)} kr` : '—'} ·
                  beskrivning {it.description_length} tecken
                </div>
                {!it.eligible && it.reasons?.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {it.reasons.map((r) => (
                      <li key={r} className="text-sm text-amber-700 flex items-start gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <Link
                to={`/admin/products?edit=${it.product_id}`}
                className="text-sm text-[#2a9d8f] hover:underline whitespace-nowrap"
                data-testid={`edit-product-link-${it.product_id}`}
              >
                Redigera →
              </Link>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="p-12 text-center text-slate-400 text-sm">Inga produkter i denna kategori.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMerchantFeed;
